import { formatMoneyAddDecimalPointAndThousands } from '../../util/PaymentUtils';
import {
  getSupplierLocationDetail,
  searchMarketplaceLocations,
  DEFAULT_SEARCH_RADIUS_MILES,
  type MarketplaceLocation,
  type SupplierLocationDetail,
} from './requests/location-search-requests';
import type {
  KnowledgeAnswer,
  KnowledgeBreakdownRow,
  KnowledgeContext,
  KnowledgeLink,
  KnowledgeQuery,
} from './knowledgeRegistry';

/**
 * "Are my prices too high?" — benchmarks one of a supplier's own locations
 * against the marketplace listings around it.
 *
 * ## Why this skill exists
 *
 * Every supplier skill so far answered from the caller's *own* data alone
 * (capacity, occupancy, revenue). A supplier asking whether they are priced
 * competitively is asking a question only *other* people's listings can answer,
 * and before this the assistant fell back to the LLM, which read the supplier's
 * own export and compared their locations to *each other* — never to the market.
 *
 * This skill closes that gap deterministically: it anchors on one of the
 * supplier's locations, pulls the public "Find A Space" listings within a radius
 * of it (the same endpoint {@link searchMarketplaceLocations} the buyer search
 * uses), and states in exact numbers how the supplier's daily and monthly rates
 * sit against the local median and range. Because the arithmetic is done here
 * rather than summarised by a model, the numbers can be trusted and carry no
 * "double-check this" disclaimer.
 *
 * ## Anchoring
 *
 * A benchmark needs a single point to search around, so this answers for one
 * location at a time. When the message names a location (or the account has just
 * one) that is the anchor; when several are in scope it asks which to compare
 * rather than firing a search per yard. The anchor's coordinates are not in the
 * bulk-edit export — address fields are excluded from it — so the location
 * detail endpoint is read for the point, its own prices and its features.
 */

/** Miles around the anchor to gather comparable listings. Matches the buyer
 *  search default so "nearby" means the same thing everywhere. */
const RADIUS_MILES = DEFAULT_SEARCH_RADIUS_MILES;

/** Most differentiating features to name when justifying a premium. */
const MAX_DIFFERENTIATORS = 4;

/** A feature is a selling point when strictly fewer than this share of nearby
 *  listings also offer it — i.e. it is genuinely uncommon locally. */
const RARE_FEATURE_SHARE = 0.5;

/** The median of a non-empty list of numbers (mean of the two middle values for
 *  an even count). Callers guard against the empty case. */
const median = (values: number[]): number => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
};

/** Cents → "$1,200.00". Rounds first so a fractional median reads cleanly. */
const money = (cents: number): string => formatMoneyAddDecimalPointAndThousands(Math.round(cents));

const pluralize = (count: number, singular: string): string =>
  `${count} ${singular}${count === 1 ? '' : 's'}`;

/** A location's display name — its own name, else a neutral fallback. */
const nameOf = (detail: SupplierLocationDetail): string =>
  detail.locationName?.trim() || 'your location';

/** "City, ST", "City", or '' when neither is known. */
const placeOf = (detail: SupplierLocationDetail): string =>
  [detail.city?.trim(), detail.state?.trim()].filter(Boolean).join(', ');

/** A finite, positive price in cents, or null when the field is absent/zero. */
const priceOf = (value: number | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;

/**
 * Coerces a coordinate to a finite number, or null when it is absent/unparseable.
 *
 * The location detail endpoint returns latitude/longitude as **strings** (the
 * map components across the app `parseFloat` them), so a bare `typeof === number`
 * check would reject every real location and decline to benchmark. This accepts
 * either a number or a numeric string and rejects only genuinely-missing data.
 */
const toCoordinate = (value: number | string | null | undefined): number | null => {
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : null;
};

/** Normalises a feature label for set membership: trimmed, lower-cased. */
const normalizeFeature = (feature: string): string => feature.trim().toLowerCase();

interface PriceComparison {
  /** The anchor's own rate, in cents. */
  own: number;
  median: number;
  min: number;
  max: number;
  /** How many nearby listings published this rate. */
  count: number;
}

/** Builds the comparison for one rate kind, or null when the anchor has no such
 *  rate or no nearby listing publishes one. */
const compare = (
  ownPrice: number | null,
  competitorPrices: number[],
): PriceComparison | null => {
  if (ownPrice === null || competitorPrices.length === 0) return null;
  return {
    own: ownPrice,
    median: median(competitorPrices),
    min: Math.min(...competitorPrices),
    max: Math.max(...competitorPrices),
    count: competitorPrices.length,
  };
};

/** How the anchor sits against the local median, as a plain word. */
const standing = (comparison: PriceComparison): 'above' | 'below' | 'in line with' => {
  if (comparison.own > comparison.median) return 'above';
  if (comparison.own < comparison.median) return 'below';
  return 'in line with';
};

/** One breakdown row for a rate kind: the anchor's price and where it sits. */
const comparisonRow = (label: string, comparison: PriceComparison): KnowledgeBreakdownRow => {
  const where = standing(comparison);
  const median = `local median ${money(comparison.median)}`;
  const range = `range ${money(comparison.min)}\u2013${money(comparison.max)}`;
  return {
    label,
    value: `${money(comparison.own)} \u00b7 ${where} the ${median} \u00b7 ${range}`,
  };
};

/**
 * Features the anchor offers that few nearby listings do — the concrete reasons
 * a higher price can be defended. A feature counts as a differentiator when a
 * minority of competitors also list it; the rarer ones come first.
 */
const differentiators = (
  ownFeatures: string[],
  competitors: MarketplaceLocation[],
): string[] => {
  if (competitors.length === 0) return [];

  const owned = ownFeatures.map(normalizeFeature).filter(Boolean);
  const unique = Array.from(new Set(owned));

  const share = (feature: string): number =>
    competitors.filter((competitor) =>
      (competitor.features ?? []).some((f) => normalizeFeature(f) === feature),
    ).length / competitors.length;

  return unique
    .map((feature) => ({ feature, share: share(feature) }))
    .filter((entry) => entry.share < RARE_FEATURE_SHARE)
    .sort((a, b) => a.share - b.share)
    .slice(0, MAX_DIFFERENTIATORS)
    .map((entry) => entry.feature);
};

/** The in-app search page centred on the anchor, so the supplier can see the
 *  same nearby listings on the map and read each one. */
const mapLink = (detail: SupplierLocationDetail): KnowledgeLink => ({
  label: 'See these nearby locations on the map',
  path: `/yards?lat=${detail.addressLatitude}&lng=${detail.addressLongitude}`,
});

/**
 * Turns the anchor detail and the nearby listings into the benchmark answer.
 * Split out from {@link answer} so the wording can be tested without any
 * network.
 */
export const describeBenchmark = (
  detail: SupplierLocationDetail,
  competitors: MarketplaceLocation[],
): KnowledgeAnswer => {
  const name = nameOf(detail);
  const place = placeOf(detail);

  if (competitors.length === 0) {
    return {
      text:
        `I could not find other marketplace locations within ${RADIUS_MILES} miles of ` +
        `**${name}**${place ? ` in ${place}` : ''} to compare against right now. ` +
        'Once more nearby yards are listed I can benchmark your rates against them.',
      link: mapLink(detail),
    };
  }

  const dayCompetitors = competitors
    .map((c) => priceOf(c.pricePerDay))
    .filter((p): p is number => p !== null);
  const monthCompetitors = competitors
    .map((c) => priceOf(c.pricePerMonth))
    .filter((p): p is number => p !== null);

  const dayComparison = compare(priceOf(detail.pricePerDay), dayCompetitors);
  const monthComparison = compare(priceOf(detail.pricePerMonth), monthCompetitors);

  const breakdown: KnowledgeBreakdownRow[] = [];
  if (dayComparison) breakdown.push(comparisonRow('Your daily rate', dayComparison));
  if (monthComparison) breakdown.push(comparisonRow('Your monthly rate', monthComparison));
  breakdown.push({
    label: 'Nearby locations',
    value: `${pluralize(competitors.length, 'listing')} within ${RADIUS_MILES} miles`,
  });

  // The premium is worth defending only where the supplier actually sits above
  // the market, so the differentiators are surfaced then and framed to explain.
  const abovePrice =
    (dayComparison && standing(dayComparison) === 'above') ||
    (monthComparison && standing(monthComparison) === 'above');
  const standout = differentiators(detail.features ?? [], competitors);
  if (abovePrice && standout.length > 0) {
    breakdown.push({ label: 'Few nearby yards offer', value: standout.join(', ') });
  }

  // No comparable rate on either kind: the anchor lists neither a daily nor a
  // monthly price, or none of the neighbours do. Say so rather than imply a
  // comparison happened.
  if (!dayComparison && !monthComparison) {
    return {
      text:
        `I found ${pluralize(competitors.length, 'location')} within ${RADIUS_MILES} miles of ` +
        `**${name}**, but I could not line up a comparable daily or monthly rate \u2014 either ` +
        `**${name}** has no published rate or the nearby listings do not. Set your daily and ` +
        'monthly rates on the location and I can benchmark them.',
      breakdown,
      link: mapLink(detail),
    };
  }

  const headline = ((): string => {
    const parts: string[] = [];
    if (dayComparison) {
      parts.push(
        `your daily rate of **${money(dayComparison.own)}** is ${standing(dayComparison)} the ` +
          `local median of **${money(dayComparison.median)}**`,
      );
    }
    if (monthComparison) {
      parts.push(
        `your monthly rate of **${money(monthComparison.own)}** is ${standing(monthComparison)} the ` +
          `local median of **${money(monthComparison.median)}**`,
      );
    }
    return parts.join(', and ');
  })();

  const premiumNote =
    abovePrice && standout.length > 0
      ? ` Where you charge more, lean on what few nearby yards offer \u2014 ${standout.join(', ')} \u2014 to justify it.`
      : '';

  return {
    text:
      `Compared with ${pluralize(competitors.length, 'location')} within ${RADIUS_MILES} miles of ` +
      `**${name}**${place ? ` in ${place}` : ''}, ${headline}.${premiumNote}`,
    breakdown,
    link: mapLink(detail),
  };
};

/** Lists the in-scope locations by name so the supplier can pick one to compare. */
const askWhichLocation = (context: KnowledgeContext): KnowledgeAnswer => {
  const names = context.targets.map((t) => `**${t.name}**`);
  const list =
    names.length <= 3
      ? names.join(', ')
      : `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`;
  return {
    text:
      'I benchmark one location at a time against the listings around it. ' +
      `Which should I compare \u2014 ${list}? For example _"how do my prices compare at ${context.targets[0].name}"_.`,
  };
};

const answer = async (context: KnowledgeContext): Promise<KnowledgeAnswer> => {
  if (context.targets.length !== 1) {
    if (context.targets.length === 0) {
      return { text: 'You do not have any locations to compare yet.' };
    }
    return askWhichLocation(context);
  }

  const anchor = context.targets[0];
  const detailResponse = await getSupplierLocationDetail(anchor.id);
  const detail = detailResponse?.body ?? {};

  const lat = toCoordinate(detail.addressLatitude);
  const lng = toCoordinate(detail.addressLongitude);
  if (lat === null || lng === null) {
    return {
      text:
        `I don't have map coordinates on file for **${anchor.name}**, so I can't pull the nearby ` +
        'listings needed to compare prices. Once its address is set with a location on the map I ' +
        'can benchmark it.',
    };
  }

  const response = await searchMarketplaceLocations(lat, lng, RADIUS_MILES);
  const competitors = (response?.body ?? []).filter(
    (location) => location.visible !== false && location.locationId !== anchor.id,
  );

  // Fill any price/feature gaps in the detail from the anchor's own marketplace
  // row when it appears in the results, so the comparison is not blocked by a
  // detail payload that happens to omit a field the listing publishes.
  const ownListing = (response?.body ?? []).find((l) => l.locationId === anchor.id);
  const enriched: SupplierLocationDetail = {
    ...detail,
    pricePerDay: priceOf(detail.pricePerDay) ?? ownListing?.pricePerDay,
    pricePerMonth: priceOf(detail.pricePerMonth) ?? ownListing?.pricePerMonth,
    features: detail.features?.length ? detail.features : ownListing?.features,
  };

  return describeBenchmark(enriched, competitors);
};

/**
 * Supplier-only. Buyers do not set prices, and the buyer-facing marketplace
 * search ({@link locationSearchQuery}) already answers "what's available near
 * X"; this is the supplier's own competitiveness question and reads their
 * private location detail.
 */
export const priceBenchmarkQuery: KnowledgeQuery = {
  id: 'priceBenchmark',
  audiences: ['supplier'],
  synonyms: [
    'are my prices too high',
    'are my prices too low',
    'is my price too high',
    'is my price too low',
    'am i priced too high',
    'am i priced too low',
    'am i overpriced',
    'am i underpriced',
    'am i charging too much',
    'am i charging too little',
    'am i priced competitively',
    'are my prices competitive',
    'are my rates competitive',
    'how competitive are my prices',
    'how competitive are my rates',
    'how do my prices compare',
    'how do my prices compare to others',
    'how does my price compare',
    'how does my pricing compare',
    'how do my rates compare',
    'how does my rate compare',
    'how do my prices stack up',
    'how does my location compare',
    'how does this location compare',
    'compare my prices',
    'compare my pricing',
    'compare my rates',
    'compare my price',
    'compare my location',
    'compare this location',
    'price comparison',
    'pricing comparison',
    'market rate for my location',
    'am i charging enough',
  ],
  requires: [
    ['compare', 'compares', 'compared', 'comparison', 'competitive', 'competitively', 'overpriced', 'underpriced', 'stack'],
    ['price', 'prices', 'pricing', 'priced', 'rate', 'rates', 'charge', 'charging', 'cost', 'location'],
  ],
  example: 'are my prices too high compared to others',
  description:
    'Benchmarks the supplier\u2019s OWN location prices against the public marketplace listings ' +
    'near it, stating how their daily and monthly rates sit versus the local median and range, ' +
    'and which of their features few nearby yards offer. Answers "are my prices too high", "how ' +
    'do my prices compare", and "how does my location compare". This is about the supplier\u2019s ' +
    'own competitiveness \u2014 never a buyer browsing locations to book, never editing a price, ' +
    'and never the supplier\u2019s revenue or occupancy.',
  answer,
};

export const __testing = {
  median,
  compare,
  standing,
  differentiators,
  describeBenchmark,
};
